import { useCallback, useEffect } from 'react';
import { UseNavigateResult, useRouter } from '@tanstack/react-router';
import { Authn, ClientTokens, HandleCallbackProps } from '@frontend/auth';
import { SignInMethod } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { hydraShellService } from '../services';
import { getRedirectURL } from '../services/fetch-wrapper.ts';
import { ContentLoader } from './content-loader.tsx';
import { FormWrap } from './form-wrap';
import { Layout } from './layout';

interface Props<T extends string> {
  auth: Authn;
  navigate: UseNavigateResult<T>;
  signInMethod: SignInMethod;
  getAuthCallbackProps?: () => HandleCallbackProps | undefined;
}

export const SignInCallback = <T extends string>({ auth, navigate, signInMethod, getAuthCallbackProps }: Props<T>) => {
  const { t } = useTranslation('SignIn');
  const router = useRouter();

  const handleCallback = useCallback(async () => {
    if (!auth.isUserAuthenticated()) {
      auth.changeAuthMethod(signInMethod);

      let callbackProps: HandleCallbackProps | undefined = undefined;
      if (getAuthCallbackProps) {
        callbackProps = getAuthCallbackProps();
      }

      let redirectURL: string | undefined;
      try {
        const tokens: ClientTokens = await auth.handleCallback(callbackProps);
        const initialLoginResponse = await hydraShellService.initializeOAuth2Flow();

        redirectURL = !!initialLoginResponse.requestUrl
          ? getRedirectURL(initialLoginResponse.requestUrl) ?? appConfig.DEFAULT_REDIRECT
          : appConfig.DEFAULT_REDIRECT;

        const acceptLoginResponse = await hydraShellService.acceptOAuth2Flow(tokens.weaveToken || '');
        window.location.href = acceptLoginResponse.redirectTo || appConfig.DEFAULT_REDIRECT;
      } catch (e: any) {
        console.error(e);
        window.location.href = redirectURL || appConfig.DEFAULT_REDIRECT;
      }
    } else {
      navigate({ to: `/` });
    }
  }, []);

  useEffect(() => {
    handleCallback();
  }, [router.history, handleCallback]);

  return (
    <Layout background={'blue'}>
      <FormWrap>
        <ContentLoader message={t('Logging in...')} show />
      </FormWrap>
    </Layout>
  );
};
